import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants } from 'common/helpers'

import type { RecommendationsPageState, Source } from 'pages/recommendations/types'
import type { QuizResult } from 'pages/recommendations/RecommendationsQuizPage/types'

import ratedRecommendationsQuery from './ratedRecommendationsQuery.graphql'
import recommendationsLayoutQuery from './recommendationsLayoutQuery.graphql'
import historyRecommendationsQuery from './historyRecommendations.graphql'
import selectionsRecommendationsQuery from './selectionsRecommendations.graphql'


const update            = (data) => reducers.models.recommendations.update(data)
const updateQuiz        = (data) => reducers.models.recommendations.update({ quiz: data })
const updateRated       = (data) => reducers.models.recommendations.update({ rated: data })
const updateHistory     = (data) => reducers.models.recommendations.update({ history: data })
const updateSelections  = (data) => reducers.models.recommendations.update({ selections: data })

type FetchLayoutDataInput = {
  withQuiz?: boolean
  withRated?: boolean
  withHistory?: boolean
  withSelections?: boolean
}

type DataKey = 'history' | 'quiz' | 'rated' | 'selections'

type ResponseData = {
  [K in DataKey]: Source
}

const modifyRecommendationsBySource = (data: ResponseData): RecommendationsPageState => {
  if (!data) {
    return null
  }

  return Object.keys(data).reduce((result, key: DataKey) => {
    const source: Source = data[key]

    // there is "recommendationsCount" in source. We need it to show notifications to user about
    // how many recommendations he has (but dont' fetch groups, so it's possible that "groups" will be empty
    // when "recommendationsCount" > 0
    let isEmpty: boolean

    if (!source.groups || !source.groups.length) {
      isEmpty = true
    }
    else {
      isEmpty = !source.groups.reduce((count, { products }) => count + (!products ? 0 : products.length), 0)
    }

    result[key] = {
      ...source,
      isEmpty,
      isFetching: false,
      isFetched: true,
    }

    return result
  }, {} as RecommendationsPageState)
}

const fetchLayoutData = (data: FetchLayoutDataInput = {}): void => {
  const { withQuiz = false, withRated = false, withHistory = false, withSelections = false } = data

  reducers.models.recommendations.setFetchingStatuses({
    isFetching: true,
    quiz: { isFetching: withQuiz },
    rated: { isFetching: withRated },
    history: { isFetching: withHistory },
    selections: { isFetching: withSelections },
  })

  request.post('graphql?name=recommendationsLayout', {
    body: {
      query: recommendationsLayoutQuery,
      variables: {
        withQuiz,
        withRated,
        withHistory,
        withSelections,
        sort: 'USER_VOTE',
      },
    },
    abortPrevious: true,
    withErrorModal: true,
    modifyResult: (data) => modifyRecommendationsBySource(data),
  })
    .then((modifiedData) => {
      update({ ...modifiedData, isFetching: false, isFetched: true })
    }, () => {})
}

// RATED

const updateRatedRecommendations = ({ offset = 0 } = {}) => {
  const { models: { recommendations: { rated: { groups } } } } = getState()

  updateRated({ isFetching: true })

  request.post('graphql?name=selectionRecommendations', {
    body: {
      query: ratedRecommendationsQuery,
      variables: {
        offset,
      },
    },
    withErrorModal: false,
    modifyResult: (data) => modifyRecommendationsBySource(data),
  })
    .then(({ rated }) => {
      rated.isFetching = false
      rated.groups = groups.concat(rated.groups)

      updateRated(rated)
    }, () => {
      updateRated({ isFetching: false })
    })
}

// HISTORY

/**
 * fetch only recommendation result
 * it's needed to update only recommendations feed on recommendations/history page
 * after user rate a fragrance to avoid of removing "unrated" product that was rated just now
 *
 * if products are requested then user saw recommendations and we should reset flag 'newRecommendationsNotification'
 *
 * @param {Boolean} isNotificationOnly - request notification flag only to check if there are new recommendations for user
 */
const updateHistoryRecommendations = ({ isNotificationOnly = false } = {}) => {
  updateHistory({ isUpdatingRecommendations: true })

  request.post('graphql?name=historyRecommendations', {
    body: {
      query: historyRecommendationsQuery,
      variables: {
        isNotificationOnly,
        input: {
          type: 'HISTORY',
          inGroupLimit: 20,
          hideOutOfStock: true,
          deliveredOrdersOnly: true,
          resetNewRecommendationsNotification: !isNotificationOnly,
          sort: 'USER_VOTE',
        },
      },
    },
    abortPrevious: true,
    withErrorModal: false,
    modifyResult: (data) => modifyRecommendationsBySource(data),
  })
    .then(({ history }) => {
      history.isUpdatingRecommendations = false

      updateHistory(history)

      if (history.newRecommendationsNotification) {
        actions.misc.notifications.show('newRecommendations')
      }
    }, () => {
      updateHistory({ isUpdatingRecommendations: false })
    })
}

// QUIZ

const setRetakingQuiz = (isRetaking: boolean = true) => updateQuiz({ isRetaking })

const saveQuizResult = (quizResult: QuizResult) => {
  updateQuiz({
    isFetching: true,
    isRetaking: false,
  })

  return request.post('rest/recommendations-quiz/answer', {
    body: quizResult,
    withErrorModal: true,
  })
    .then(
      () => {
        actions.misc.cookies.removeItem(constants.cookieNames.unregisteredUserQuizData)

        // update quiz flag to fetch results
        actions.models.me.updateFinishedQuizNew('recommendations', true)

        fetchLayoutData({ withQuiz: true })
      },
      () => {
        actions.misc.cookies.setSessionItem(constants.cookieNames.unregisteredUserQuizData, quizResult)
      }
    )
}

// SELECTIONS

const updateSelectionsRecommendations = () => {
  updateSelections({ isUpdatingRecommendations: true })

  request.post('graphql?name=selectionRecommendations', {
    body: {
      query: selectionsRecommendationsQuery,
      variables: {
        sort: 'USER_VOTE',
      },
    },
    abortPrevious: true,
    withErrorModal: false,
    modifyResult: (data) => modifyRecommendationsBySource(data),
  })
    .then(({ selections }) => {
      selections.isUpdatingRecommendations = false

      updateSelections(selections)
    }, () => {
      updateSelections({ isUpdatingRecommendations: false })
    })
}


export default {
  update,
  updateQuiz,
  updateHistory,
  updateSelections,

  fetchLayoutData,
  updateRatedRecommendations,
  updateHistoryRecommendations,
  updateSelectionsRecommendations,

  setRetakingQuiz,
  saveQuizResult,
}
